import React, { useState, useEffect, useCallback } from 'react';
import { callsApi, Call } from '../services/api';
import './Calls.css';

const Calls: React.FC = () => {
  const [calls, setCalls] = useState<Call[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });
  const [filters, setFilters] = useState({
    campaign: '',
    status: '',
    startDate: '',
    endDate: ''
  });

  const fetchCalls = useCallback(async () => {
    try {
      setLoading(true);
      const response = await callsApi.getCalls({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });

      setCalls(response.data.calls);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        pages: response.data.pagination.pages
      }));
    } catch (error) {
      console.error('Error fetching calls:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return '-';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = (status: string) => {
    const statusClass = status === 'completed' ? 'success' :
                       status === 'in-progress' ? 'warning' :
                       status === 'no-answer' ? 'danger' : 'default';

    return <span className={`status-badge ${statusClass}`}>{status}</span>;
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      campaign: '',
      status: '',
      startDate: '',
      endDate: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  return (
    <div className="calls-page">
      <div className="calls-header">
        <h1>Call Log</h1>
        <div className="calls-filters">
          <input
            type="text"
            placeholder="Campaign"
            value={filters.campaign}
            onChange={(e) => handleFilterChange('campaign', e.target.value)}
          />
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="in-progress">In Progress</option>
            <option value="no-answer">No Answer</option>
            <option value="busy">Busy</option>
          </select>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
          <button onClick={clearFilters} className="clear-filters-btn">
            Clear
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading calls...</div>
      ) : (
        <>
          <div className="calls-table-container">
            <table className="calls-table">
              <thead>
                <tr>
                  <th>Date/Time</th>
                  <th>Direction</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Tracking #</th>
                  <th>Campaign</th>
                  <th>Status</th>
                  <th>Duration</th>
                  <th>Source</th>
                  <th>Recording</th>
                </tr>
              </thead>
              <tbody>
                {calls.map((call) => (
                  <tr key={call.id}>
                    <td>{formatDateTime(call.started_at)}</td>
                    <td>
                      <span className={`direction-badge ${call.source === 'outbound-dialer' ? 'outbound' : 'inbound'}`}>
                        {call.source === 'outbound-dialer' ? 'Outbound' : 'Inbound'}
                      </span>
                    </td>
                    <td>{call.from_number}</td>
                    <td>{call.to_number}</td>
                    <td>{call.tracking_number}</td>
                    <td>{call.campaign_name || '-'}</td>
                    <td>{getStatusBadge(call.status)}</td>
                    <td>{formatDuration(call.duration)}</td>
                    <td>{call.utm_source || '-'}</td>
                    <td>
                      {call.recording_url ? (
                        <a
                          href={call.recording_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="recording-link"
                        >
                          🎵 Play
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
              className="pagination-btn"
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {pagination.page} of {pagination.pages} ({pagination.total} total)
            </span>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page === pagination.pages}
              className="pagination-btn"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Calls;